<script setup lang="ts">
import { TRACK_EVENT_DESCRIPTIONS } from '@/constants/track-events';
import { useDisposes } from '@telecom/shared/composables/use-disposes';
import {
    formatTrackEventDetail,
    formatTrackEventTimestamp,
    type TrackEventDebugRecord,
    useTrackEventDebug,
} from '@/presentation/composables/use-track-event-debug';
import { nextTick, onBeforeUnmount, ref, watch } from 'vue';

defineOptions({
    name: 'TrackEventDebugPanel',
});

const {
    enabled,
    records,
    isExpanded,
    onRecord,
    clearRecords,
    toggleExpanded,
} = useTrackEventDebug();
const scrollContainer = ref<HTMLDivElement | null>(null);
const showingAllDetailRows = ref(new Set<string>());

const TRACK_EVENT_BUBBLE_LIFETIME = 2000;
const TRACK_EVENT_BUBBLE_FADE_DURATION = 300;

interface TrackEventBubble {
    id: number;
    sourceType: string;
    description: string;
}

const bubbles = ref<TrackEventBubble[]>([]);
const bubbleTimers = new Map<number, number>();
let nextBubbleId = 0;
const { disposes } = useDisposes();

function getSourceTypeDescription(sourceType?: string): string {
    return sourceType ? TRACK_EVENT_DESCRIPTIONS[sourceType] || '' : '';
}

async function scrollToLatestEvent() {
    await nextTick();
    if (scrollContainer.value) {
        scrollContainer.value.scrollTop = scrollContainer.value.scrollHeight;
    }
}

function removeBubble(id: number) {
    const timer = bubbleTimers.get(id);
    if (timer !== undefined) {
        window.clearTimeout(timer);
        bubbleTimers.delete(id);
    }

    bubbles.value = bubbles.value.filter((bubble) => bubble.id !== id);
}

function clearBubbles() {
    bubbleTimers.forEach((timer) => window.clearTimeout(timer));
    bubbleTimers.clear();
    bubbles.value = [];
}

function enqueueBubble(record: TrackEventDebugRecord) {
    if (isExpanded.value) {
        return;
    }

    const id = ++nextBubbleId;
    bubbles.value.unshift({
        id,
        sourceType: record.request.source_type || '—',
        description: getSourceTypeDescription(record.request.source_type),
    });

    const timer = window.setTimeout(
        () => removeBubble(id),
        TRACK_EVENT_BUBBLE_LIFETIME - TRACK_EVENT_BUBBLE_FADE_DURATION,
    );
    bubbleTimers.set(id, timer);
}

function getRecordKey(timestamp: number, index: number): string {
    return `${timestamp}-${index}`;
}

function isShowingAllFields(key: string): boolean {
    return showingAllDetailRows.value.has(key);
}

function toggleAllFields(key: string) {
    const nextKeys = new Set(showingAllDetailRows.value);

    if (nextKeys.has(key)) {
        nextKeys.delete(key);
    } else {
        nextKeys.add(key);
    }

    showingAllDetailRows.value = nextKeys;
}

function clearEventRecords() {
    clearRecords();
    showingAllDetailRows.value = new Set();
    clearBubbles();
}

watch(() => records.value.length, scrollToLatestEvent);
watch(isExpanded, (value) => {
    if (value) {
        clearBubbles();
        scrollToLatestEvent();
    }
});
watch(enabled, (value) => {
    if (!value) {
        clearBubbles();
    }
});

disposes.push(onRecord(enqueueBubble));
onBeforeUnmount(clearBubbles);
</script>

<template>
    <Teleport to="body">
        <div
            v-if="enabled && !isExpanded"
            data-testid="track-event-debug-panel"
            class="pointer-events-auto fixed bottom-4 right-4 z-[100001]"
            aria-label="埋点信息调试面板"
        >
            <TransitionGroup
                v-if="bubbles.length"
                tag="div"
                name="track-event-bubble"
                class="pointer-events-none absolute bottom-full right-0 mb-2 flex w-max max-w-[calc(100vw-2rem)] flex-col-reverse items-end gap-2"
                role="status"
                aria-live="polite"
            >
                <div
                    v-for="bubble in bubbles"
                    :key="bubble.id"
                    data-testid="track-event-debug-bubble"
                    class="w-max max-w-[min(420px,calc(100vw-2rem))] rounded-lg bg-gray-90 px-3 py-2 text-right text-white shadow-[0_4px_16px_rgba(0,0,0,0.2)]"
                >
                    <div class="max-w-full truncate font-mono text-xs">
                        {{ bubble.sourceType }}
                    </div>
                    <div v-if="bubble.description" class="mt-0.5 max-w-full truncate text-xs text-gray-20">
                        {{ bubble.description }}
                    </div>
                </div>
            </TransitionGroup>

            <button
                data-testid="track-event-debug-toggle"
                type="button"
                class="flex items-center gap-2 rounded-lg border border-gray-30 bg-white px-3 py-2 text-sm font-medium text-gray-90 shadow-[0_4px_16px_rgba(0,0,0,0.18)] hover:bg-gray-10"
                :aria-expanded="isExpanded"
                aria-label="展开或收起埋点信息面板"
                @click="toggleExpanded"
            >
                <span>埋点</span>
                <span class="rounded-full bg-eureka-500 px-2 py-0.5 text-xs text-white">
                    {{ records.length }}
                </span>
            </button>
        </div>

        <section
            v-else-if="enabled"
            data-testid="track-event-debug-panel"
            class="pointer-events-auto fixed bottom-4 right-4 z-[100001] flex h-[480px] w-[min(1120px,calc(100vw-2rem))] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-lg border border-gray-30 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.18)]"
            aria-label="埋点信息调试面板"
        >
            <header class="flex shrink-0 items-center justify-between gap-4 border-b border-gray-30 bg-gray-10 px-3 py-2">
                <div class="flex min-w-0 items-center gap-2 text-sm font-medium text-gray-90">
                    <span>埋点信息</span>
                    <span class="rounded-full bg-eureka-500 px-2 py-0.5 text-xs text-white">
                        {{ records.length }}
                    </span>
                </div>

                <div class="flex shrink-0 items-center gap-2">
                    <button
                        v-if="isExpanded"
                        type="button"
                        class="text-xs text-gray-60 hover:text-gray-90"
                        @click.stop="clearEventRecords"
                    >
                        清空
                    </button>
                    <button
                        type="button"
                        class="rounded px-1 text-xs text-gray-60 hover:bg-gray-20 hover:text-gray-90"
                        :aria-expanded="isExpanded"
                        aria-label="展开或收起埋点信息面板"
                        @click.stop="toggleExpanded"
                    >
                        {{ isExpanded ? '收起' : '展开' }}
                    </button>
                </div>
            </header>

            <div v-if="isExpanded" ref="scrollContainer" class="min-h-0 flex-1 overflow-auto">
                <div v-if="!records.length" class="px-6 py-10 text-center text-sm text-gray-60">
                    暂无埋点事件
                </div>

                <table v-else class="min-w-[900px] table-fixed border-collapse text-xs text-gray-90">
                    <thead class="sticky top-0 z-[1] bg-gray-10 text-left text-gray-60">
                        <tr>
                            <th class="w-[96px] border-b border-gray-30 px-3 py-2 font-medium">
                                时间
                            </th>
                            <th class="w-[220px] border-b border-gray-30 px-3 py-2 font-medium">
                                source_type
                            </th>
                            <th class="w-[180px] border-b border-gray-30 px-3 py-2 font-medium">
                                event_type
                            </th>
                            <th class="border-b border-gray-30 px-3 py-2 font-medium">
                                event_detail_str
                            </th>
                            <th class="w-[160px] border-b border-gray-30 px-3 py-2 font-medium">
                                other_param
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr
                            v-for="(record, index) in records"
                            :key="`${record.timestamp}-${index}`"
                            class="align-top odd:bg-white even:bg-gray-10"
                        >
                            <td class="whitespace-nowrap border-b border-gray-20 px-3 py-2 font-mono">
                                {{ formatTrackEventTimestamp(record.timestamp) }}
                            </td>
                            <td class="break-all border-b border-gray-20 px-3 py-2">
                                <div class="font-mono">
                                    {{ record.request.source_type || '—' }}
                                </div>
                                <div
                                    v-if="getSourceTypeDescription(record.request.source_type)"
                                    class="mt-1 text-gray-60"
                                >
                                    {{ getSourceTypeDescription(record.request.source_type) }}
                                </div>
                            </td>
                            <td class="break-all border-b border-gray-20 px-3 py-2 font-mono">
                                {{ record.request.event_type || '—' }}
                            </td>
                            <td class="border-b border-gray-20 px-3 py-2">
                                <pre class="max-h-[180px] overflow-auto whitespace-pre-wrap break-all font-mono leading-5">{{ formatTrackEventDetail(record.request.event_detail_str, isShowingAllFields(getRecordKey(record.timestamp, index))) }}</pre>
                                <button
                                    v-if="record.request.event_detail_str"
                                    type="button"
                                    class="mt-2 text-xs text-eureka-600 hover:text-eureka-700"
                                    @click.stop="toggleAllFields(getRecordKey(record.timestamp, index))"
                                >
                                    {{ isShowingAllFields(getRecordKey(record.timestamp, index)) ? '隐藏通用字段' : '查看全部字段' }}
                                </button>
                            </td>
                            <td class="break-all border-b border-gray-20 px-3 py-2 font-mono">
                                {{ record.request.other_param || '—' }}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </section>
    </Teleport>
</template>

<style scoped>
.track-event-bubble-enter-active,
.track-event-bubble-leave-active {
    transition: opacity 300ms ease, transform 300ms ease;
}

.track-event-bubble-enter-from,
.track-event-bubble-leave-to {
    opacity: 0;
    transform: translateY(8px) scale(0.96);
}

.track-event-bubble-move {
    transition: transform 200ms ease;
}
</style>
